import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Shield, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/authContext";

const ROLE_OPTIONS = [
  { value: "super_admin", label: "Super-admin", desc: "Acesso total" },
  { value: "pastor", label: "Pastor", desc: "Resolve só atribuições próprias" },
  { value: "volunteer", label: "Voluntário", desc: "Vê só novo + andamento, não escala" },
];

export default function AdminUsersSettings() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // email currently being updated

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get("/inbox/admins/list");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar admins.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChange = async (email, newRole) => {
    if (busy) return;
    setBusy(email);
    try {
      await api.post("/inbox/admins/role", { email, admin_role: newRole });
      setItems((arr) => arr.map((u) => u.email === email ? { ...u, admin_role: newRole } : u));
      toast.success("Papel atualizado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível alterar.");
    } finally { setBusy(null); }
  };

  return (
    <section data-testid="admin-users-settings" className="bg-white border border-border rounded-2xl p-5 sm:p-6">
      <header className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold flex items-center gap-1.5">
            <Shield className="w-3 h-3" /> Equipe pastoral
          </p>
          <h3 className="font-display text-xl text-navy mt-1">Papéis administrativos</h3>
          <p className="text-[12px] text-navy/55 mt-1.5 max-w-md">
            Defina o nível de acesso de cada admin. <strong>Super-admin</strong> tem acesso total,
            <strong> pastor</strong> resolve apenas suas próprias atribuições, <strong>voluntário</strong> só
            vê itens em aberto e não pode escalar.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="admin-users-refresh"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border text-[11px] uppercase tracking-widest text-navy/75 hover:border-gold/60 transition-colors"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          Atualizar
        </button>
      </header>

      {loading ? (
        <div className="text-center text-navy/55 py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Carregando…</div>
      ) : items.length === 0 ? (
        <p className="text-sm text-navy/55">Nenhum administrador cadastrado.</p>
      ) : (
        <ul className="divide-y divide-border" data-testid="admin-users-list">
          {items.map((u) => {
            const isSelf = u.id === me?.id;
            return (
              <li key={u.email} data-testid={`admin-user-row-${u.email}`} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-navy truncate flex items-center gap-2">
                    {u.name || u.email}
                    {isSelf && <span className="text-[10px] uppercase tracking-widest text-gold">você</span>}
                  </p>
                  <p className="text-[11px] text-navy/55 truncate">{u.email}</p>
                </div>
                <select
                  value={u.admin_role || "super_admin"}
                  onChange={(e) => handleChange(u.email, e.target.value)}
                  disabled={busy === u.email}
                  data-testid={`admin-user-role-${u.email}`}
                  className="bg-[hsl(var(--parchment))] border border-border rounded-full px-3 py-1.5 text-[12px] focus:bg-white focus:border-gold/60 outline-none transition-colors min-w-[180px]"
                >
                  {ROLE_OPTIONS.map((o) => (
                    <option
                      key={o.value}
                      value={o.value}
                      disabled={isSelf && o.value === "volunteer"}
                    >
                      {o.label}, {o.desc}
                    </option>
                  ))}
                </select>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
